import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GAME_HAND_PHASE, PLAYER_STATUS } from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';

// Seat statuses that mean "paused, and the I'm Back control applies": sat out
// manually (1), auto-sat-out after missing actions (6), or stood up to observe
// (2). Mirrors the set SitOutOverlay uses to decide whether to show at all.
const PAUSED_STATUSES = [
  +PLAYER_STATUS.SITTING_OUT,
  +PLAYER_STATUS.FOR_SITOUT,
  +PLAYER_STATUS.LEAVING
];

// `hand` values the ENGINE treats as the dealer-button / blind rotation (see
// apps/engine/games/common/constants.js and table.isRotationAssignment).
//
// From GAME_HAND_PHASE, which mirrors the engine's numbering. NOT from the
// `GAME_HAND` map in the same file: that one has drifted below DEAL_FLOP and
// would put the deal inside the rotation. The engine writes `gameTable.hand`,
// so its numbering is what arrives on the wire.
const ROTATION_HANDS: number[] = [
  GAME_HAND_PHASE.GAME_PREP,
  GAME_HAND_PHASE.SETUP_DEALER,
  GAME_HAND_PHASE.SETUP_SMALL_BLIND,
  GAME_HAND_PHASE.SETUP_BIG_BLIND
];

// Statuses the engine's `checkIfSittingOut` covers. This is the exception that
// lets `returnTable` through EVEN mid-rotation, so a manually sat-out or
// observing player never has to wait (games/common/game.js).
const ACCEPTED_MID_ROTATION = [
  +PLAYER_STATUS.SITTING_OUT,
  +PLAYER_STATUS.LEAVING,
  +PLAYER_STATUS.EXIT_TABLE
];

// How long to wait before giving the control back. There is no ack for
// `returnTable` (the engine registers it with no callback, unlike sitout which
// replies on `sitoutRsp`), so the only confirmation available is the seat status
// arriving on the next table broadcast. This bounds the wait TWICE — once for
// the table to become acceptable, then again for the confirmation — so a long
// rotation cannot eat the confirmation's budget. Giving the button back is the
// honest failure mode.
const RETURN_CONFIRM_TIMEOUT_MS = 5000;

/**
 * The engine's `table.isRotationAssignment`, client-side.
 *
 * Note the missing-value case. The engine's helper is declared `function
 * (hand = 0)`, so an absent `hand` becomes GAME_PREP and therefore IS a
 * rotation hand; `null` reaches the same answer through `+null === 0`. Only the
 * empty string (GAME_NOT_STARTED) short-circuits to false. Mirror that rather
 * than reading a missing value as "no rotation": that is the permissive
 * direction, and being permissive here means sending a press the engine then
 * silently refuses, which is the failure this whole module exists to avoid.
 */
export const isRotationAssignment = (hand: unknown): boolean => {
  if (hand === '') return false;
  const value = hand === null || hand === undefined ? GAME_HAND_PHASE.GAME_PREP : +hand;
  return ROTATION_HANDS.includes(value);
};

/**
 * Whether the engine will act on `returnTable` for this seat RIGHT NOW.
 *
 * This is `texas.socket.returnTable`'s own gate, mirrored:
 *
 *   (!isRotationAssignment(hand) || checkIfSittingOut(seatId, gameTable)) && seatId > 0
 *
 * One precondition is deliberately NOT mirrored: `checkIfSittingOut` also
 * requires `getPlayerGUID(seatId, gameTable) != ''`. The client cannot be
 * looser than the engine on that, because `currentPlayer.seatId` is itself
 * derived by the reducer from a players[] GUID match, so a seatId above 0
 * already implies the POV player's GUID is on that seat.
 *
 * Mirroring it matters because the refusal is silent: the engine returns
 * without touching the seat and without replying, so a press sent when this is
 * false is simply lost. The client therefore has to know the same rule.
 *
 * Deliberately NOT `gameState.canExitTable`, which an earlier pass used.
 * `canExitTable` is a TABLE-wide flag with exceptions unrelated to this seat
 * (`!isRotation || noPlayers === 1 || (satOutPlayers === 1 && newPlayers === 1)
 * || ...`), so it is true mid-rotation on a heads-up or all-sat-out table. An
 * auto-sat-out (6) player pressing in that window took the send-now path, the
 * engine dropped it, and nothing retried — the overlay had already hidden and
 * the seat status never changed, so it never came back either. `canExitTable`
 * still gates whether TableActions renders the corner control, which is what it
 * is for; it just cannot stand in for this.
 */
export const engineAcceptsReturn = (
  hand: unknown,
  seatStatus: unknown,
  seatId: unknown
): boolean => {
  if (!(+(seatId as any) > 0)) return false;
  return !isRotationAssignment(hand) || ACCEPTED_MID_ROTATION.includes(+(seatStatus as any));
};

/**
 * Press handler for "I'm Back".
 *
 * Sends immediately whenever the engine will take it — which, thanks to the
 * `checkIfSittingOut` exception, includes mid-rotation for a manually sat-out
 * or observing player. Only the cases the engine really would refuse get held:
 * an auto-sat-out (6) seat during the rotation, or a seat that has not hydrated
 * yet. The old code mirrored the refusal with a `hand === '' || +hand > 3` check
 * that dropped the click entirely, which is why the button looked dead during a
 * transition.
 */
export const useImBackRequest = () => {
  const { imBack } = useGameService();
  const { setIsSatOut, setIsStandingUp, setPendingImBack } = useContext(GameContext);
  const hand = useTableSelector((table) => table.game.hand);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const seatStatus = useTableSelector((table) => (table.game as any)[`p${seatId}status`]);

  return useCallback(() => {
    if (engineAcceptsReturn(hand, seatStatus, seatId)) {
      // Send now and clear the flags so the overlay and the corner control both
      // drop immediately.
      imBack(seatId);
      setIsSatOut(false);
      setIsStandingUp(false);
      return;
    }
    // The engine would refuse. Hold the send and let `pendingImBack` hide the
    // overlay, so the press reads as accepted. Deliberately do NOT clear
    // isSatOut / isStandingUp here: leaving them set is what lets the overlay
    // come back untouched if the backstop has to abandon the hold.
    setPendingImBack(true);
  }, [hand, seatStatus, seatId, imBack, setIsSatOut, setIsStandingUp, setPendingImBack]);
};

/**
 * Owns the held "I'm Back" press: flushes it when the engine will accept it,
 * confirms it landed, and gives the control back if it did not.
 *
 * Must be mounted somewhere that outlives the button. SitOutOverlay returns
 * null the moment the press is accepted, so anything living inside it unmounts
 * before it could flush. Game.tsx is the natural host: one instance per table,
 * including one per cell in the grid, so the hold is scoped per table for free.
 * It cannot live in GameProvider either, since useGameService consumes
 * GameContext and that would be circular.
 *
 * The hold is per-table by construction and cannot outlive the table it was
 * made at: `pendingImBack` is state inside GameProvider, which Game.tsx and
 * MultiTableGrid both key by gameID, so any table change (a tournament
 * balancing move included) remounts this subtree and resets the hold. A press
 * made in the balancing window is therefore lost, and the overlay reappears at
 * the new table — the same honest failure mode as the backstop.
 */
export const useDeferredImBack = () => {
  const { imBack } = useGameService();
  const { pendingImBack, setPendingImBack } = useContext(GameContext);
  const hand = useTableSelector((table) => table.game.hand);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const seatStatus = useTableSelector((table) => (table.game as any)[`p${seatId}status`]);

  // Guards the emit rather than the hold: `pendingImBack` stays true until the
  // seat is confirmed back, so without this a second broadcast arriving before
  // that confirmation would send `returnTable` twice. The engine does not
  // de-duplicate it. State rather than a ref so the backstop below can re-arm
  // when the emit goes out; it flips at most once per press.
  const [hasSent, setHasSent] = useState(false);

  const cancelHold = useCallback(() => {
    setHasSent(false);
    setPendingImBack(false);
  }, [setPendingImBack]);

  // Flush: the engine will accept it now. This is what makes the deferral fix
  // the auto-sat-out case — once the rotation is over the engine's outer gate
  // passes for every paused status rather than only the ones
  // `checkIfSittingOut` covers.
  //
  // The seat guard is inside engineAcceptsReturn (the engine wants seatId > 0),
  // and `isSeated` is checked on top of it: right after a seat change the
  // table's state arrives in pieces, and sending `&pos=0` would have the engine
  // act against the wrong seat. Both are reasons to wait, not to abandon.
  useEffect(() => {
    if (!pendingImBack || hasSent) return;
    if (!isSeated) return;
    if (!engineAcceptsReturn(hand, seatStatus, seatId)) return;
    setHasSent(true);
    imBack(seatId);
  }, [pendingImBack, hasSent, isSeated, hand, seatStatus, seatId, imBack]);

  // Confirm: the seat left the paused statuses, so the engine took it. Release
  // the hold and let SitOutOverlay's own status mirror clear the flags.
  useEffect(() => {
    if (!pendingImBack) return;
    const status = +seatStatus;
    if (!Number.isFinite(status) || PAUSED_STATUSES.includes(status)) return;
    cancelHold();
  }, [pendingImBack, seatStatus, cancelHold]);

  // Backstop. Depends on `pendingImBack` and `hasSent` only, so a burst of
  // broadcasts cannot keep restarting the clock — `hasSent` flips once per
  // press, which deliberately restarts it at the moment the emit goes out so
  // waiting for the rotation cannot consume the confirmation's budget. The
  // callback is read through a ref to stay current without re-arming the timer.
  const cancelHoldRef = useRef(cancelHold);
  useEffect(() => {
    cancelHoldRef.current = cancelHold;
  }, [cancelHold]);

  useEffect(() => {
    if (!pendingImBack) return undefined;
    const timer = setTimeout(() => cancelHoldRef.current(), RETURN_CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pendingImBack, hasSent]);
};
